import { utils, writeFile } from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Constants
const CURRENCY_DEFAULT = '£';
const DATE_FORMAT_FALLBACK = 'N/A';
const LOGO_PATH = "assets/main-logo.jpg";

// Utility Functions
const parseAmount = (amount, currency) => {
  let parsedCurrency = currency;
  let parsedAmount = amount;

  if (typeof amount === 'string' && amount.startsWith('£')) {
    parsedCurrency = '£';
    parsedAmount = amount.replace('£', '').trim();
  }

  return {
    currency: parsedCurrency || CURRENCY_DEFAULT,
    amount: parseFloat(parsedAmount).toFixed(2)
  };
};

const formatDate = (dateField) => {
  if (!dateField) return DATE_FORMAT_FALLBACK;

  const date = new Date(dateField);
  if (isNaN(date.getTime())) return dateField;

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

const escapeCSVValue = (value) => {
  if (typeof value === "string") {
    return `"${value.replace(/"/g, '""')}"`; 
  }
  return value;
};

// Format mapping by tag
const formatters = {
  dashboard: (item, formattedAmount) => ({
    "Name": item.name || 'Unknown',
    "Stage": item.stage || "N/A",
    "Total Amount": formattedAmount,
  }),

  "post-prelba": (item, formattedAmount) => ({
    "Debtor Name": item.debtor,
    "Total Amount": formattedAmount,
    "Date": item.date,
    "Risk": item.risk
  }),

  ai: (item, formattedAmount) => ({
    "Debtor Name": item.name,
    "Total Amount": formattedAmount,
    "Date": item.lastPayment,
    "AI Score": item.aiScore,
    "Risk": item.risk
  }),
  lawyerdashboard: (item, formattedAmount) => ({
    "Debtor Name": item.case_details,
    "Client Name": item.client_name,
    "Status":item.status
    
  }),
 comm: (item, formattedAmount) => ({
    "Date & Time": item.createdAt,
    "Description": item.description,
    "Sender" : item.userName
    
  }),

  default: (item, formattedAmount, formattedDate) => ({
    "Debtor's Name": item.name || 'Unknown',
    "Status": item.status,
    "Amount Due": formattedAmount,
    "Submission Date": formattedDate
  })
};

// Format Table Data
const formatTableData = (data, tag) => {
  return data.map((item) => {
    const { currency, amount } = parseAmount(item.amount, item.currency);
    const formattedAmount = `${currency} ${amount}`;
    const formattedDate = formatDate(item.dueDate || item.createdAt);

    const formatter = formatters[tag] || formatters.default;
    return formatter(item, formattedAmount, formattedDate);
  });
};

// Export Functions
const exportToExcel = (data, tag) => {
  const formattedData = formatTableData(data, tag);
  const worksheet = utils.json_to_sheet(formattedData);
  const workbook = utils.book_new();
  utils.book_append_sheet(workbook, worksheet, "Data");
  writeFile(workbook, "data.xlsx");
};

const exportToCSV = (data, tag) => {
  const formattedData = formatTableData(data, tag);

  const csvContent = [
    Object.keys(formattedData[0]).map(escapeCSVValue).join(","),
    ...formattedData.map((row) =>
      Object.values(row).map(escapeCSVValue).join(",")
    ),
  ].join("\n");

  const blob = new Blob(["\ufeff" + csvContent], { 
    type: "text/csv;charset=utf-8;" 
  });
  const url = URL.createObjectURL(blob);
  downloadFile(url, "data.csv");
};

const exportToPDF = (data, tag) => {
  const formattedData = formatTableData(data, tag);
  const doc = new jsPDF();
  const img = new Image();
  img.src = LOGO_PATH;

  img.onload = () => {
    // Add logo
    doc.addImage(img, "PNG", 10, 10, 40, 20);

    // Center the title
    doc.setFontSize(16);
    const pageWidth = doc.internal.pageSize.getWidth();
    const textWidth = doc.getTextWidth("Report");
    doc.text("Report", (pageWidth - textWidth) / 2, 35);

    // Add table
    autoTable(doc, {
      head: [Object.keys(formattedData[0])],
      body: formattedData.map((row) => Object.values(row)),
      startY: 45,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 245, 245] }
    });

    doc.save("data.pdf");
  };

  img.onerror = () => {
    console.error("Failed to load logo image");
    // Still generate PDF without logo
    doc.setFontSize(16);
    const pageWidth = doc.internal.pageSize.getWidth();
    const textWidth = doc.getTextWidth("Report");
    doc.text("Report", (pageWidth - textWidth) / 2, 20);

    autoTable(doc, {
      head: [Object.keys(formattedData[0])],
      body: formattedData.map((row) => Object.values(row)),
      startY: 30,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 245, 245] }
    });

    doc.save("data.pdf");
  };
};

const downloadFile = (url, filename) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url); // Clean up
};

// Main Export Function
export const exportDataTable = (data, format, tag) => {
  if (!data || data.length === 0) {
    alert("No data to export.");
    return;
  }

  const exportHandlers = {
    Excel: () => exportToExcel(data, tag),
    CSV: () => exportToCSV(data, tag),
    PDF: () => exportToPDF(data, tag)
  };

  const handler = exportHandlers[format];
  
  if (handler) {
    handler();
  } else {
    alert("Invalid format selected.");
  }
};